import { api } from '../api/client';
import { API_CARD_DEFS_URL, API_COLLECTIONS_URL, API_ELEMENT_SET_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * The card definition's pre-save question: what would saving this element as
 * driven (calculated, or driven from another card) do to the values already
 * stored on its items? Same authority as the save, nothing written. `elements`
 * is the UNSAVED element list so a formula types against what the save will
 * store. A 400 here is the 400 the save would answer — surfaced inline by the
 * caller rather than after the round trip.
 *
 * Returns { total, withValues, kind, sample: [{ _id, title, current, next }] }.
 */
export async function previewDrivenChange( cardId, { elementName, elements, elementChoices = null } ) {
	// The person's answer to an ambiguous element name in a driven filter (utils/elementRefusal.js).
	const { data } = await api.post( `${API_CARD_DEFS_URL}/${cardId}/driven-preview`, withElementChoicesField( { elementName, elements }, elementChoices ) );
	return data || { total: 0, withValues: 0, sample: [] };
}

/**
 * "Try it on" (KPR-959): what an UNSAVED related driven element gives for one
 * item of the card, and the source items it read —
 * `POST /api/card-definitions/{id}/driven-item-preview` (keepr-api SCHEMA.md
 * § Driven fields, "Try it on"). Same authority as the save, nothing written.
 * `itemId` empty asks for the most recently saved item the caller can read.
 *
 * Returns { collectionId, item: { _id, title, card_id } | null, overridden,
 * value, refused, incomplete, sources: { count, visible, door, doorReason } |
 * null }. A 400 is the save's own refusal; 429 the preview's rate limit.
 */
export async function previewDrivenItem( cardId, { elementName, elements, itemId = '' } ) {
	const { data } = await api.post( `${API_CARD_DEFS_URL}/${cardId}/driven-item-preview`, { elementName, elements, ...( itemId ? { itemId } : {} ) } );
	return data || null;
}

/**
 * "What would saving this do?" — `POST /api/card-definitions/{id}/change-preview`,
 * the SAME payload as the PATCH, the same gates, nothing written
 * (keepr-api SCHEMA.md § Change preview). The card editor asks it before a
 * save that removes or retypes an element or detaches an element set, and
 * shows the counts (delete audit F9).
 *
 * Returns { card, wouldApply, refusal, changes: [{ kind, element, dataType?,
 * from?, to?, conversion?, itemsWithValues?, destructive? }], cardFields,
 * sideEffects: [{ code, element?, message }], destructive, summary } — or
 * null when the server sent nothing to read. A refusal is a 200 with
 * `wouldApply: false`; only the write gate itself answers 403/404.
 */
export async function previewCardChange( cardId, payload ) {
	const response = await api.post( `${API_CARD_DEFS_URL}/${cardId}/change-preview`, payload );
	return response?.data || null;
}

/**
 * The element set editor's twin: `POST /api/element-sets/{id}/change-preview`,
 * the same payload as `PATCH /api/element-sets/{id}`. Returns { set,
 * wouldApply, refusal, cards, changes: [{ kind: 'removed' | 'retyped',
 * element, dataType?, from?, to?, itemsWithValues, destructive }],
 * sideEffects, destructive, summary } — `itemsWithValues` counted over
 * every card that carries the set — or null.
 */
export async function previewElementSetChange( setId, payload ) {
	const response = await api.post( `${API_ELEMENT_SET_URL( setId )}/change-preview`, payload );
	return response?.data || null;
}

/**
 * Values under elements a collection card no longer has stay on its items,
 * hidden (removing an element, or detaching a set, never deletes them).
 * `POST /api/card-definitions/{id}/purge-hidden-values` removes them — one
 * logged change, revertable from the card's History.
 *
 *   dryRun: true   → { dryRun, items, values, elements: [{ element, items }], skipped }
 *   dryRun: false  → the same counts plus `changes` (the change ids written)
 *
 * `elements` (names) limits the purge; absent means every hidden element.
 * Session only (an API key is 403), collection cards only (400
 * `purge_collection_cards_only` for a global card).
 */
export async function purgeHiddenValues( cardId, { dryRun = true, elements = undefined } = {} ) {
	const body = { dryRun };
	if ( Array.isArray( elements ) ) body.elements = elements;
	const { data } = await api.post( `${API_CARD_DEFS_URL}/${cardId}/purge-hidden-values`, body );
	return data || null;
}

/**
 * Retire a GLOBAL card, or put it back (keepr-api KPR-280, the owner's ruling
 * G14: retiring replaces deleting a global card in use) —
 * `POST /api/card-definitions/{id}/retire` / `…/unretire`, an admin's act. A
 * retired card leaves the Card Library and takes no new adopters; every
 * collection that holds it keeps working. Returns `{ payload, changed }` —
 * `changed: false` when it already stood where it was asked to be.
 */
export async function retireCard( cardId ) {
	const { data } = await api.post( `${API_CARD_DEFS_URL}/${cardId}/retire` );
	return data || null;
}

export async function unretireCard( cardId ) {
	const { data } = await api.post( `${API_CARD_DEFS_URL}/${cardId}/unretire` );
	return data || null;
}

/**
 * The repeating groups a card of this collection may copy (KPR-859) —
 * `GET /api/collections/{id}/group-sources`: every group the collection's
 * cards define, then every group a starter installed here supplied. Each row
 * is `{ kind, source, card, starter?, group, columns, minEntries, maxEntries,
 * sections, element, unresolved? }`; `element` is ready to add to a draft and
 * `source` is what the save's `copyGroups` sends back (utils/groupCopy.js).
 * A manager's, in a session, while repeating groups are switched on.
 */
export async function listGroupSources( collectionId ) {
	const { data } = await api.get( `${API_COLLECTIONS_URL}/${collectionId}/group-sources` );
	return Array.isArray( data?.sources ) ? data.sources : [];
}
